import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { BASE_URL } from "../base-url";
import { NotificationsQueryParams } from "../../interfaces/notifications/notifications-query-params.interface";
import { SharedNotificationsQueryParams, SharedNotificationsResponse } from "../../interfaces/notifications/shared-notification.interface";
import { IdName } from "../../interfaces/id-name";
import { firstValueFrom } from 'rxjs';
import { TokenService } from '../../services/token.service';
import { ServiceEnum } from '../../enums/service.enum';

@Injectable({
    providedIn: 'root'
})
export class NotificationsService {
    private http = inject(HttpClient);
    private tokenService = inject(TokenService);

    notificationCounts = signal<any>(null);

    async fetchNotificationCounts(applicationDataNumber?: string): Promise<any> {
        if (this.tokenService.isAuthenticated()) {
            const counts = await firstValueFrom(
                this.http.get(`${BASE_URL}account/getnotificationcounts?applicationDataNumber=${applicationDataNumber ? applicationDataNumber : ''}`)
            );
            this.notificationCounts.set(counts);
            return counts;
        }
    }

    getNotifications(params: NotificationsQueryParams) {
        const httpParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== null && value !== undefined && value !== '')
        );
        return this.http.get(`${BASE_URL}Admin/Notifications`, { params: httpParams });
    }

    getSharedNotifications(params: SharedNotificationsQueryParams) {
        const httpParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) =>
                value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && !value.length)
            )
        );
        return this.http.get<SharedNotificationsResponse>(`${BASE_URL}account/notifications`, { params: httpParams });
    }

    getServices() {
        return this.http.get<IdName<number>[]>(`${BASE_URL}account/services`);
    }

    readSharedNotification(serviceId: number, notificationId: number) {
        return serviceId === ServiceEnum.Outbound
            ? this.http.post(`${BASE_URL}OutboundApplicationData/notifications/${notificationId}/read`, {})
            : this.http.post(`${BASE_URL}ApplicationData/notifications/${notificationId}/read`, {});
    }

    getSenders() {
        return this.http.get<IdName<number>[]>(`${BASE_URL}Admin/Notifications/senders`);
    }

    getRecievers() {
        return this.http.get<IdName<number>[]>(`${BASE_URL}Admin/Notifications/recievers`);
    }

    getNotificationOriginTypes() {
        return this.http.get<IdName<number>[]>(`${BASE_URL}Admin/Notifications/origin-types`);
    }

    getNotification(id: number) {
        return this.http.post(`${BASE_URL}Admin/notifications/${id}/read`, {});
    }

    getUserNotification(id: number) {
        return this.http.post(`${BASE_URL}ApplicationData/notifications/${id}/read`, {});
    }

    sendNotification(data: any, id: string) {
        return this.http.post(`${BASE_URL}admin/application-data/${id}/manual-notification`, data)
    }

    getNotificationTemplates() {
        return this.http.get(`${BASE_URL}Admin/get-notifications`);
    }

    getOutboundNotificationTemplates() {
        return this.http.get(`${BASE_URL}outboundapplicationdataadmin/get-notifications`);
    }

    editNotificationTemplate(data: any) {
        return this.http.post(`${BASE_URL}admin/edit-notifications`, data)
    }

    editOutboundNotificationTemplate(data: any) {
        return this.http.post(`${BASE_URL}outboundapplicationdataadmin/edit-notifications`, data)
    }

    getNotificationTemplateData(id: any) {
        return this.http.get(`${BASE_URL}Admin/get-notification/${id}`);
    }

    getOutboundNotificationTemplateData(id: any) {
        return this.http.get(`${BASE_URL}outboundapplicationdataadmin/get-notification/${id}`);
    }

    getOutboundNotificationTemplateDataUser(id: any) {
        return this.http.get(`${BASE_URL}outboundapplicationdata/get-notification/${id}`);
    }
}
